import { nextPage } from "@/api/paging";
import { Fragment, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { listUsers, searchAudit, type AuditEvent, type User } from "@/api/client";
import { useSession } from "@/auth/session";
import { formatDate } from "@/components/format";
import { Field, inputClass, submitClass } from "@/components/form";
import { EmptyState, ErrorBanner, WorkspaceHeader } from "@/components/ui";
import {
  ENTITY_TYPES,
  actorLabel,
  entityLink,
  entityText,
  entityTypeLabel,
  fromLocalInput,
  toLocalInput,
  utcLabel,
} from "@/features/audit/format";
import type { AuditSearchParams } from "@/routes/audit";
import { ScrollArea } from "@/components/ScrollArea";
import { PagedFooter } from "@/components/Pagination";
import { pageParams } from "@/routes/paging";

const auditApi = getRouteApi("/staff/audit");

// Bounds the actor list's paging; staff accounts number in the tens.
const ACTOR_PAGES = 20;

export async function listActorChoices() {
  const items: User[] = [];
  for (let page = 1; page <= ACTOR_PAGES; page++) {
    const next = await listUsers({ page, pageSize: 100 });
    items.push(...(next.items ?? []));
    if (nextPage(next) === undefined) return { items, truncated: false };
  }
  return { items, truncated: true };
}

type Filters = Omit<AuditSearchParams, "page" | "page_size" | "event">;

export function AuditSearch() {
  const search = auditApi.useSearch();
  const { action, entity_type, entity_id, actor, from, to, page, page_size, event } = search;
  const filters: Filters = { action, entity_type, entity_id, actor, from, to };
  const navigate = useNavigate();
  // A reversed range from a hand-edited URL is shown as a problem rather than sent.
  const reversed = Boolean(from && to && from > to);

  const events = useQuery({
    queryKey: ["audit", { ...filters, page, page_size }],
    queryFn: () => {
      const params: Parameters<typeof searchAudit>[0] = pageParams(search);
      if (action) params.action = action;
      if (entity_type) params.entityType = entity_type;
      if (entity_id) params.entityId = entity_id;
      if (actor) params.actorUserId = actor;
      if (from) params.from = from;
      if (to) params.to = to;
      return searchAudit(params);
    },
    enabled: !reversed,
  });
  const items = events.data?.items ?? [];
  const filtered = items.find((i) => i.entity_type === entity_type && i.entity_id === entity_id);
  // Until an event for the record arrives, name only its type.
  const chipText = filtered ? entityText(filtered) : entityTypeLabel(entity_type ?? "");

  const toggle = (id: number) =>
    void navigate({ to: "/audit", search: { ...filters, page, page_size, event: event === id ? undefined : id } });

  return (
    <section className="space-y-5">
      <WorkspaceHeader title="Audit log" detail="Who did what, and when; newest first" />
      {/* key resets the form when back/forward changes the filters */}
      <AuditFilters
        key={JSON.stringify(filters)}
        filters={filters}
        onApply={(next) => void navigate({ to: "/audit", search: { ...next, page_size } })}
      />
      {entity_type && entity_id ? (
        <p className="flex flex-wrap items-center gap-2 text-sm">
          <span className="rounded-full bg-zinc-100 px-3 py-1 text-xs font-medium text-zinc-700">
            Filtered to {chipText}
          </span>
          <button
            type="button"
            onClick={() =>
              void navigate({ to: "/audit", search: { ...filters, entity_type: undefined, entity_id: undefined, page_size } })
            }
            className="text-xs font-medium underline"
          >
            Remove filter
          </button>
        </p>
      ) : null}
      {reversed ? <ErrorBanner error={new Error("From must be before To.")} /> : <ErrorBanner error={events.error} />}

      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
        {events.isLoading ? (
          <EmptyState title="Searching" detail="Looking up audit events." />
        ) : events.isSuccess && items.length === 0 ? (
          <EmptyState title="No audit events match" detail="Widen the time range or clear a filter." />
        ) : (
          <ScrollArea label="Audit events">
            <table aria-label="Audit events" className="w-full min-w-[860px] text-left text-sm">
              <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase text-zinc-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Time</th>
                  <th className="px-4 py-3 font-medium">Actor</th>
                  <th className="px-4 py-3 font-medium">Action</th>
                  <th className="px-4 py-3 font-medium">Entity</th>
                  <th className="px-4 py-3 font-medium">IP</th>
                  <th className="px-4 py-3 font-medium">
                    <span className="sr-only">Details</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {items.map((item) => (
                  <Fragment key={item.id}>
                    <EventRow event={item} open={event === item.id} onToggle={() => toggle(item.id)} />
                    {event === item.id ? (
                      <tr>
                        <td colSpan={6} className="bg-zinc-50 px-4 py-3">
                          <EventDetails event={item} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        )}
        <PagedFooter data={events.data} page={page} pageSize={page_size} />
      </div>

    </section>
  );
}

function EventRow({ event, open, onToggle }: { event: AuditEvent; open: boolean; onToggle: () => void }) {
  const { can } = useSession();
  const link = entityLink(event, can);
  const entity = entityText(event);
  return (
    <tr className="hover:bg-zinc-50">
      <td className="px-4 py-3 text-zinc-500">
        <time dateTime={event.occurred_at} title={utcLabel(event.occurred_at)}>
          {formatDate(event.occurred_at)}
        </time>
      </td>
      <td className="px-4 py-3 text-zinc-700">
        {event.actor_user_id && can("user.read") ? (
          <Link to="/users/$userId" params={{ userId: event.actor_user_id }} className="underline-offset-2 hover:underline">
            {actorLabel(event)}
          </Link>
        ) : (
          actorLabel(event)
        )}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-zinc-800">{event.action}</td>
      <td className="px-4 py-3 text-xs text-zinc-700">
        {link ? (
          <Link {...link} className="underline-offset-2 hover:underline">
            {entity}
          </Link>
        ) : (
          <span>{entity}</span>
        )}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-zinc-500">{event.ip_address ?? "-"}</td>
      <td className="px-4 py-3 text-right">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={`audit-event-${event.id}`}
          onClick={onToggle}
          className="text-xs font-medium text-zinc-700 underline"
        >
          Details
        </button>
      </td>
    </tr>
  );
}

function EventDetails({ event }: { event: AuditEvent }) {
  return (
    <section id={`audit-event-${event.id}`} aria-label={`Event ${event.id} details`} className="space-y-2 text-xs">
      <p className="text-zinc-600">
        {entityTypeLabel(event.entity_type)} id: <span className="font-mono text-zinc-800">{event.entity_id}</span>{" "}
        <Link
          to="/audit"
          search={{ entity_type: event.entity_type, entity_id: event.entity_id }}
          className="font-medium text-zinc-800 underline"
        >
          Show history
        </Link>
      </p>
      <p className="text-zinc-600">
        Request id: <span className="font-mono text-zinc-800">{event.request_id ?? "-"}</span>
      </p>
      {/* Rendered as text, so metadata can never inject markup. */}
      <pre className="max-h-72 overflow-auto rounded-md border border-zinc-200 bg-white p-3 font-mono text-zinc-800">
        {JSON.stringify(event.metadata ?? {}, null, 2)}
      </pre>
    </section>
  );
}

function AuditFilters({ filters, onApply }: { filters: Filters; onApply: (next: Filters) => void }) {
  const { can } = useSession();
  const readsUsers = can("user.read");
  const users = useQuery({
    queryKey: ["users", { audit: true, all: true }],
    queryFn: listActorChoices,
    enabled: readsUsers,
  });
  const [values, setValues] = useState({
    action: filters.action ?? "",
    entity_type: filters.entity_type ?? "",
    actor: filters.actor ?? "",
    from: toLocalInput(filters.from),
    to: toLocalInput(filters.to),
  });
  const [rangeProblem, setRangeProblem] = useState<string>();
  const set = (field: keyof typeof values, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setRangeProblem(undefined);
  };
  const options = users.data?.items ?? [];
  // An actor from the URL who isn't among the listed users still shows as chosen.
  const unlisted = values.actor && !options.some((u) => u.id === values.actor) ? values.actor : null;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const from = fromLocalInput(values.from);
    const to = fromLocalInput(values.to);
    if (from && to && from > to) {
      setRangeProblem("must be after From");
      return;
    }
    const text = (value: string) => value.trim() || undefined;
    onApply({
      action: text(values.action),
      entity_type: text(values.entity_type),
      // The record filter is set from an event; changing the type drops it.
      entity_id: values.entity_type === (filters.entity_type ?? "") ? filters.entity_id : undefined,
      actor: text(values.actor),
      from,
      to,
    });
  }

  const input = (field: "action", label: string, placeholder: string) => (
    <Field label={label}>
      {(props) => (
        <input {...props} value={values[field]} placeholder={placeholder} onChange={(e) => set(field, e.target.value)} className={inputClass} />
      )}
    </Field>
  );

  return (
    <form role="search" aria-label="Audit filters" noValidate onSubmit={submit} className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <div className="grid gap-3 md:grid-cols-3">
        {input("action", "Action", "e.g. user.password_set")}
        <Field label="Entity type">
          {(props) => (
            <select {...props} value={values.entity_type} onChange={(e) => set("entity_type", e.target.value)} className={inputClass}>
              <option value="">Any type</option>
              {ENTITY_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          )}
        </Field>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        {readsUsers && !users.isError ? (
          <Field
            label="Actor"
            hint={users.data?.truncated ? `Only the first ${options.length} users are listed.` : undefined}
          >
            {(props) => (
              <select {...props} value={values.actor} onChange={(e) => set("actor", e.target.value)} className={inputClass}>
                <option value="">Anyone</option>
                {unlisted ? <option value={unlisted}>{unlisted}</option> : null}
                {options.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.full_name}
                  </option>
                ))}
              </select>
            )}
          </Field>
        ) : (
          <Field label="Actor user id">
            {(props) => (
              <input {...props} value={values.actor} onChange={(e) => set("actor", e.target.value)} className={inputClass} />
            )}
          </Field>
        )}
        <Field label="From">
          {(props) => (
            <input {...props} type="datetime-local" value={values.from} onChange={(e) => set("from", e.target.value)} className={inputClass} />
          )}
        </Field>
        <Field label="To" problem={rangeProblem}>
          {(props) => (
            <input {...props} type="datetime-local" value={values.to} onChange={(e) => set("to", e.target.value)} className={inputClass} />
          )}
        </Field>
      </div>
      {readsUsers && users.isError ? (
        <p role="status" className="text-xs text-amber-700">
          Users could not be loaded, so the actor list is unavailable; enter an id instead.
        </p>
      ) : null}
      <p className="text-xs text-zinc-500">Times are in your local time zone.</p>
      <button type="submit" className={submitClass}>
        Apply filters
      </button>
    </form>
  );
}
